import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { API_URL } from "@/lib/api/client";
import { queryKeys } from "@/hooks/queries";
import { onVehiclePatch, vehiclesService, type TraccarDevice } from "@/services/vehicles.service";
import type { TraccarPosition } from "@/services/positions.service";
import type { Vehicle } from "@/types";

// O Traccar empurra atualizações de dispositivo/posição/evento em tempo
// real por WebSocket (mesma sessão/cookie do login), assim que processa um
// novo relatório do rastreador — sem esperar o próximo poll. Antes disso, a
// única forma de saber que a ignição/movimento mudou era o poll de 15s do
// useVehicles()/MapPage, ou seja, até 15s de atraso.
//
// Cada mensagem traz só o que mudou (devices/positions no MESMO formato
// bruto do GET /api/devices e /api/positions — protocolo documentado do
// Traccar), nunca a frota inteira. Antes, QUALQUER mensagem — mesmo de 1
// veículo só — disparava invalidateQueries(["vehicles"]), forçando um
// refetch completo (GET /api/devices + /api/positions da frota inteira) e
// reconstruindo os 5000 objetos Vehicle do zero, mesmo que só 1 tivesse
// mudado de verdade. Gargalo real medido no teste de carga de 5000
// veículos (2026-09-02): ~1.7-2s de bloqueio da main thread por mensagem,
// ~10.000 nós de DOM recriados pra 1 veículo mudando. Agora, aplicamos um
// patch direto no cache (setQueryData) só nos veículos tocados pelo
// payload, preservando a referência dos demais objetos Vehicle — quem não
// mudou nem re-renderiza (ver VehicleMarker em routes/index.tsx). O poll de
// 15s continua ativo como rede de segurança (reconexão, mensagem perdida,
// socket bloqueado por proxy, veículo novo cadastrado noutra sessão, etc.)
// — só deixa de ser o único caminho.
//
// Formato inesperado (payload que não é JSON, ou sem devices/positions
// reconhecíveis) cai no fallback antigo: invalida ["vehicles"] inteiro. É
// deliberadamente conservador — nunca deixar a tela ficar desatualizada
// silenciosamente só porque o parsing não bateu com o formato esperado.
interface RealtimePayload {
  devices?: TraccarDevice[];
  positions?: TraccarPosition[];
}

// null = mensagem malformada (JSON inválido, ou nem string) — só esse caso
// deve cair no fallback de invalidar tudo. Um objeto válido sem
// devices/positions (ex.: mensagem só de "events") devolve arrays vazios,
// não null — não é motivo pra invalidar nada, nenhum dado de veículo mudou.
function parseRealtimePayload(raw: unknown): RealtimePayload | null {
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const devices = Array.isArray((parsed as RealtimePayload).devices)
      ? (parsed as RealtimePayload).devices
      : undefined;
    const positions = Array.isArray((parsed as RealtimePayload).positions)
      ? (parsed as RealtimePayload).positions
      : undefined;
    return { devices, positions };
  } catch {
    return null;
  }
}

function socketUrl(): string | null {
  if (!API_URL) return null;
  try {
    const u = new URL(API_URL);
    u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
    u.pathname = `${u.pathname.replace(/\/$/, "")}/api/socket`;
    u.search = "";
    return u.toString();
  } catch {
    return null;
  }
}

export function useRealtimeSync(enabled: boolean) {
  const queryClient = useQueryClient();
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Acumula devices/positions de várias mensagens dentro da mesma janela de
  // debounce (o Traccar manda um frame por tipo em sequência pra uma única
  // atualização real do dispositivo) — Map por id, então mensagens repetidas
  // do mesmo veículo dentro da janela só contam a mais recente.
  const pendingRef = useRef<{
    devices: Map<number, TraccarDevice>;
    positions: Map<number, TraccarPosition>;
  }>({ devices: new Map(), positions: new Map() });

  // Bloqueio/desbloqueio confirmado pela resposta do rastreador (ver
  // confirmBlockedFromCommand) — mesmo patch por id usado no flush abaixo.
  useEffect(
    () =>
      onVehiclePatch((updated) => {
        const patchById = new Map(updated.map((v) => [v.id, v]));
        for (const query of queryClient.getQueryCache().findAll({ queryKey: queryKeys.vehicles })) {
          queryClient.setQueryData<Vehicle[]>(query.queryKey, (prev) =>
            prev?.some((v) => patchById.has(v.id)) ? prev.map((v) => patchById.get(v.id) ?? v) : prev,
          );
        }
      }),
    [queryClient],
  );

  useEffect(() => {
    if (!enabled) return;
    const url = socketUrl();
    if (!url) return;

    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    // Fallback antigo — invalida ["vehicles"] inteiro e força refetch. Usado
    // só quando uma mensagem não bate com o formato esperado (ver
    // parseRealtimePayload) — nunca deixa a tela desatualizada em silêncio.
    const invalidateFallback = () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.vehicles });
    };

    const flush = () => {
      const { devices, positions } = pendingRef.current;
      pendingRef.current = { devices: new Map(), positions: new Map() };
      if (devices.size === 0 && positions.size === 0) return;

      const updated = vehiclesService.applyRealtimeUpdate({
        devices: [...devices.values()],
        positions: [...positions.values()],
      });
      if (updated.length === 0) return;
      const patchById = new Map(updated.map((v) => [v.id, v]));

      // Alcança tanto ["vehicles"] (MapPage) quanto ["vehicles", tick]
      // (useVehicles) — mesmo escopo por prefixo que invalidateQueries já
      // cobria antes, só que reescrevendo o array em vez de refetch.
      for (const query of queryClient.getQueryCache().findAll({ queryKey: queryKeys.vehicles })) {
        queryClient.setQueryData<Vehicle[]>(query.queryKey, (prev) => {
          if (!prev) return prev;
          let changed = false;
          const next = prev.map((v) => {
            const patch = patchById.get(v.id);
            if (!patch) return v; // referência preservada — não re-renderiza
            changed = true;
            return patch;
          });
          return changed ? next : prev;
        });
      }
    };

    const onMessage = (event: MessageEvent) => {
      const payload = parseRealtimePayload(event.data);
      if (!payload) {
        // JSON inválido/inesperado — único caso que cai no fallback antigo.
        invalidateFallback();
        return;
      }
      for (const d of payload.devices ?? []) pendingRef.current.devices.set(d.id, d);
      for (const p of payload.positions ?? []) pendingRef.current.positions.set(p.id, p);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(flush, 20);
    };

    // Tudo que aconteceu enquanto a tela "dormia" (aba em segundo plano —
    // o navegador segura os timers —, PC suspenso, internet caída, socket
    // reconectando) NÃO é reenviado pelo WebSocket. Antes só o próximo
    // tick do poll de 15s corrigia, e com a aba escondida ele nem roda
    // (refetchOnWindowFocus está desligado no QueryClient). Pedido
    // 2026-10-19: veículo aparecia online já estando offline até dar F5.
    // Agora busca a frota na hora em que a tela volta a ficar ativa.
    let lastResync = 0;
    const resyncNow = () => {
      if (stopped || document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastResync < 10_000) return;
      lastResync = now;
      invalidateFallback();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") resyncNow();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", resyncNow);
    window.addEventListener("pageshow", resyncNow);

    let hadConnection = false;
    const connect = () => {
      if (stopped) return;
      socket = new WebSocket(url);
      socket.onmessage = onMessage;
      socket.onopen = () => {
        // Reconexão (não a primeira conexão): mensagens perdidas no buraco.
        if (hadConnection) resyncNow();
        hadConnection = true;
      };
      socket.onclose = () => {
        if (stopped) return;
        reconnectTimer = setTimeout(connect, 5_000);
      };
      socket.onerror = () => socket?.close();
    };

    connect();

    // Rastreador que para de falar não gera mensagem nenhuma — o status
    // "online" só virava "offline" no próximo poll completo. Reavalia
    // localmente (sem rede) a cada 20s quem já passou do limite de tempo.
    const agingTimer = setInterval(() => {
      for (const query of queryClient.getQueryCache().findAll({ queryKey: queryKeys.vehicles })) {
        const current = queryClient.getQueryData<Vehicle[]>(query.queryKey);
        if (!current?.length) continue;
        const updated = vehiclesService.refreshAgedStatuses(current);
        if (updated.length === 0) continue;
        const patchById = new Map(updated.map((v) => [v.id, v]));
        queryClient.setQueryData<Vehicle[]>(query.queryKey, (prev) =>
          prev ? prev.map((v) => patchById.get(v.id) ?? v) : prev,
        );
      }
    }, 20_000);

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      clearInterval(agingTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", resyncNow);
      window.removeEventListener("pageshow", resyncNow);
      socket?.close();
    };
  }, [enabled, queryClient]);
}
